(() => {
  const GLOBAL_KEY = '__sixHatsBirdPersonalizationV1';
  const STORAGE_KEY = 'sixhats:bird-recommendation-v1';
  const HATS = ['white', 'red', 'black', 'yellow', 'green', 'blue'];
  const ROOT_SELECTOR = '[data-sixhats-product-showcase]';
  const rootStates = new WeakMap();

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].refresh(document);
    return;
  }

  function normalizeHandle(value) {
    if (typeof value !== 'string') return null;
    const handle = value.trim().toLowerCase();
    return /^[a-z0-9][a-z0-9-]*$/.test(handle) ? handle : null;
  }

  function normalizeRecommendation(value) {
    if (!value || typeof value !== 'object') return null;

    const primaryHat = typeof value.primaryHat === 'string' ? value.primaryHat.toLowerCase() : '';
    if (!HATS.includes(primaryHat) || !Array.isArray(value.productHandles)) return null;

    const productHandles = [];
    const seenHandles = new Set();
    value.productHandles.forEach((valueHandle) => {
      const handle = normalizeHandle(valueHandle);
      if (!handle || seenHandles.has(handle)) return;
      seenHandles.add(handle);
      productHandles.push(handle);
    });

    return {
      primaryHat,
      collectionHandle: normalizeHandle(value.collectionHandle),
      productHandles: productHandles.slice(0, 10),
    };
  }

  function storeRecommendation(recommendation) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(recommendation));
    } catch (_error) {
      // Personalization still works for this page when storage is unavailable.
    }
  }

  function readRecommendation() {
    try {
      return normalizeRecommendation(JSON.parse(sessionStorage.getItem(STORAGE_KEY)));
    } catch (_error) {
      return null;
    }
  }

  function clearRecommendation() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (_error) {
      // Reset the rendered page even when storage is unavailable.
    }
  }

  function cloneCards(cards) {
    return cards.map((card) => card.cloneNode(true));
  }

  function getRootState(root) {
    const existingState = rootStates.get(root);
    if (existingState) return existingState;

    const grid = root.querySelector('[data-sixhats-showcase-grid]');
    const eyebrow = root.querySelector('[data-sixhats-showcase-eyebrow]');
    const heading = root.querySelector('[data-sixhats-showcase-heading]');
    const viewAll = root.querySelector('[data-sixhats-showcase-view-all]');
    const slider = root.querySelector('[data-sixhats-showcase-slider]');
    const empty = root.querySelector('[data-sixhats-showcase-empty]');

    if (!grid || !slider || !empty) return null;

    const state = {
      defaultCards: cloneCards(Array.from(grid.children)),
      eyebrowText: eyebrow?.textContent || '',
      eyebrowHidden: eyebrow?.hidden ?? true,
      headingHtml: heading?.innerHTML || '',
      gridAriaLabel: grid.getAttribute('aria-label'),
      viewAllHref: viewAll?.getAttribute('href') || null,
      sliderHidden: slider.hidden,
      emptyHidden: empty.hidden,
    };

    rootStates.set(root, state);
    return state;
  }

  function capitalize(value) {
    return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
  }

  function templatesFor(root) {
    return Array.from(root.querySelectorAll('template[data-sixhats-hat-products]'));
  }

  function cardsFromTemplate(template) {
    if (!(template instanceof HTMLTemplateElement)) return [];
    return Array.from(template.content.querySelectorAll('.product-showcase__card[data-product-handle]'));
  }

  function cardHandle(card) {
    return normalizeHandle(card.getAttribute('data-product-handle'));
  }

  function personalizedCards(root, recommendation, state) {
    const primaryTemplate = root.querySelector(
      `template[data-sixhats-hat-products="${recommendation.primaryHat}"]`
    );
    if (!(primaryTemplate instanceof HTMLTemplateElement)) return [];

    const cardByHandle = new Map();
    [...state.defaultCards, ...templatesFor(root).flatMap(cardsFromTemplate)].forEach((card) => {
      const handle = cardHandle(card);
      if (handle && !cardByHandle.has(handle)) cardByHandle.set(handle, card);
    });

    const maximum = Math.min(10, Math.max(1, Number.parseInt(root.dataset.maxItems, 10) || 10));
    const selectedCards = [];
    const selectedHandles = new Set();

    function addCard(card) {
      const handle = card && cardHandle(card);
      if (!handle || selectedHandles.has(handle) || selectedCards.length >= maximum) return;
      selectedHandles.add(handle);
      selectedCards.push(card.cloneNode(true));
    }

    recommendation.productHandles.forEach((handle) => addCard(cardByHandle.get(handle)));
    cardsFromTemplate(primaryTemplate).forEach(addCard);
    return selectedCards;
  }

  function resetSlider(root) {
    const slider = root.querySelector('[data-sixhats-showcase-slider]');
    const grid = root.querySelector('[data-sixhats-showcase-grid]');
    if (!slider || !grid || slider.hidden) return;

    const update = () => {
      grid.scrollLeft = 0;
      if (typeof grid.scrollTo === 'function') grid.scrollTo({ left: 0, behavior: 'auto' });

      const previous = slider.querySelector('button[name="previous"]');
      const next = slider.querySelector('button[name="next"]');
      if (grid.children.length < 2) {
        previous?.setAttribute('disabled', 'disabled');
        next?.setAttribute('disabled', 'disabled');
        return;
      }

      previous?.setAttribute('disabled', 'disabled');
      next?.removeAttribute('disabled');
      if (typeof slider.resetPages === 'function') slider.resetPages();
    };

    update();
    requestAnimationFrame(update);
  }

  function normalizeCardIds(root) {
    const sectionId = root.dataset.sectionId || 'featured-toys';
    root.querySelectorAll('[data-sixhats-showcase-grid] > .product-showcase__card').forEach((card, index) => {
      card.id = `Slide-${sectionId}-${index + 1}`;
    });
  }

  function collectionUrl(template, recommendation) {
    const renderedUrl = template?.dataset.collectionUrl;
    if (renderedUrl) return renderedUrl;

    const handle = recommendation.collectionHandle || `${recommendation.primaryHat}-hat`;
    return `/collections/${handle}`;
  }

  function applyRecommendation(root, recommendation) {
    const state = getRootState(root);
    if (!state) return;

    const eyebrow = root.querySelector('[data-sixhats-showcase-eyebrow]');
    const heading = root.querySelector('[data-sixhats-showcase-heading]');
    const viewAll = root.querySelector('[data-sixhats-showcase-view-all]');
    const slider = root.querySelector('[data-sixhats-showcase-slider]');
    const grid = root.querySelector('[data-sixhats-showcase-grid]');
    const empty = root.querySelector('[data-sixhats-showcase-empty]');
    const announcement = root.querySelector('[data-sixhats-personalization-announcement]');
    const template = root.querySelector(
      `template[data-sixhats-hat-products="${recommendation.primaryHat}"]`
    );
    if (!grid || !slider || !empty) return;

    const hatName = capitalize(recommendation.primaryHat);
    const cards = personalizedCards(root, recommendation, state);

    if (eyebrow) {
      eyebrow.hidden = false;
      eyebrow.textContent = 'Chosen for this play style';
    }
    if (heading) heading.textContent = `${hatName} Hat picks`;
    grid.setAttribute('aria-label', `${hatName} Hat picks`);
    if (viewAll) viewAll.setAttribute('href', collectionUrl(template, recommendation));

    grid.replaceChildren(...cards);
    normalizeCardIds(root);
    slider.hidden = cards.length === 0;
    empty.hidden = cards.length !== 0;
    resetSlider(root);

    if (announcement) {
      announcement.textContent = cards.length
        ? `Featured Toys updated: ${hatName} Hat picks, chosen for this play style.`
        : `Featured Toys updated: ${hatName} Hat picks. No products are currently available in this collection.`;
    }
  }

  function restoreDefaults(root) {
    const state = getRootState(root);
    if (!state) return;

    const eyebrow = root.querySelector('[data-sixhats-showcase-eyebrow]');
    const heading = root.querySelector('[data-sixhats-showcase-heading]');
    const viewAll = root.querySelector('[data-sixhats-showcase-view-all]');
    const slider = root.querySelector('[data-sixhats-showcase-slider]');
    const grid = root.querySelector('[data-sixhats-showcase-grid]');
    const empty = root.querySelector('[data-sixhats-showcase-empty]');
    const announcement = root.querySelector('[data-sixhats-personalization-announcement]');
    if (!grid || !slider || !empty) return;

    if (eyebrow) {
      eyebrow.textContent = state.eyebrowText;
      eyebrow.hidden = state.eyebrowHidden;
    }
    if (heading) heading.innerHTML = state.headingHtml;
    if (state.gridAriaLabel === null) grid.removeAttribute('aria-label');
    else grid.setAttribute('aria-label', state.gridAriaLabel);
    if (viewAll) {
      if (state.viewAllHref === null) viewAll.removeAttribute('href');
      else viewAll.setAttribute('href', state.viewAllHref);
    }

    grid.replaceChildren(...cloneCards(state.defaultCards));
    normalizeCardIds(root);
    slider.hidden = state.sliderHidden;
    empty.hidden = state.emptyHidden;
    resetSlider(root);
    if (announcement) announcement.textContent = 'Featured Toys restored to its default daily selection.';
  }

  function rootsWithin(container) {
    if (!(container instanceof Element || container instanceof Document)) return [];
    const roots = Array.from(container.querySelectorAll(ROOT_SELECTOR));
    if (container instanceof Element && container.matches(ROOT_SELECTOR)) roots.unshift(container);
    return roots;
  }

  function refresh(container = document) {
    const recommendation = readRecommendation();
    rootsWithin(container).forEach((root) => {
      getRootState(root);
      if (recommendation) applyRecommendation(root, recommendation);
    });
  }

  function handleRecommendationReady(event) {
    const recommendation = normalizeRecommendation(event.detail);
    if (!recommendation) return;
    storeRecommendation(recommendation);
    rootsWithin(document).forEach((root) => applyRecommendation(root, recommendation));
  }

  function handleRecommendationReset() {
    clearRecommendation();
    rootsWithin(document).forEach(restoreDefaults);
  }

  function handleTeaserClick(event) {
    const trigger = event.target instanceof Element ? event.target.closest('[data-sixhats-bird-open]') : null;
    if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    document.dispatchEvent(new CustomEvent('sixhats:bird-open'));
  }

  document.addEventListener('sixhats:recommendation-ready', handleRecommendationReady);
  document.addEventListener('sixhats:recommendation-reset', handleRecommendationReset);
  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('click', handleTeaserClick);

  window[GLOBAL_KEY] = { refresh };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => refresh(document), { once: true });
  else refresh(document);
})();
